import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Checkbox, FormControlLabel, Tooltip} from '@mui/material';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import {useGetAdmissionCategoriesQuery} from '../../../../../api/services/lookupsService';
import {useUpdateStudentAdmissionCategoriesMutation} from '../../../../../api/services/studentService';
import useNotification from '../../../../../utility/hooks/useNotification';
import {categoryLabel as label, withOutOfUse} from './admissionCategoryList';

const AdmissionCategories = ({studentId, selected, readonly}) => {
    const {data: schoolCategories = []} = useGetAdmissionCategoriesQuery();
    const categories = withOutOfUse(schoolCategories, selected);
    const [updateCategories, {isSuccess, isError}] = useUpdateStudentAdmissionCategoriesMutation();
    const {showErrorNotification, showSuccessNotification} = useNotification();
    const [chosenIds, setChosenIds] = useState([]);

    useEffect(() => {
        setChosenIds((selected ?? []).map(c => c.id));
    }, [selected]);

    useEffect(() => {
        if (isError) {
            showErrorNotification('Failed to update the admissions categories');
        } else if (isSuccess) {
            showSuccessNotification('Admissions categories updated successfully');
        }
    }, [isSuccess, isError]);

    const toggle = id => setChosenIds(prev => prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]);

    if (categories.length === 0) {
        return null;
    }

    if (readonly) {
        const chosen = categories.filter(c => chosenIds.includes(c.id));

        return (
            <Box sx={{mt: 1}}>
                {chosen.length === 0
                    ? <Typography variant="body2" color="text.secondary">None recorded</Typography>
                    : <Box display="flex" flexWrap="wrap" gap={1}>
                        {chosen.map(category => (
                            <Tooltip key={category.id} title={category.description} arrow>
                                <Chip
                                    size="small"
                                    label={label(category)}
                                />
                            </Tooltip>
                        ))}
                    </Box>}
            </Box>
        );
    }

    return (
        <Box sx={{mt: 1}}>
            <Box display="flex" flexDirection="column">
                {categories.map(category => (
                    <FormControlLabel
                        key={category.id}
                        sx={{ml: category.parentId ? 3 : 0}}
                        control={
                            <Checkbox
                                size="small"
                                checked={chosenIds.includes(category.id)}
                                onChange={() => toggle(category.id)}
                            />
                        }
                        label={
                            <Tooltip title={category.description} arrow>
                                <span>{label(category)}</span>
                            </Tooltip>
                        }
                    />
                ))}
            </Box>
            <Box display="flex" justifyContent="end" pt={1}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => updateCategories({studentId, admissionCategoryIds: chosenIds})}
                >
                    Save categories
                </Button>
            </Box>
        </Box>
    );
};

AdmissionCategories.propTypes = {
    studentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    selected: PropTypes.array,
    readonly: PropTypes.bool
};

export default AdmissionCategories;
